import { useState, useEffect } from 'react'
import ReportsService from '../../../../services/reports.service'
import { useUpdateLhs, useReactQuery, useConditionalQuery } from '../../../../hooks'
import { getObjectAsArray, sortByKey, exportExcel_addSegments, getProgressionArray, fakeDelay } from '../../../../util'

const CONFIG = {
  DriversProgression3Level: {
    showLegends: true,
    filterType: 'periods',
  },
  SearchDriversProgression3Level: {
    type: 'SearchKeywords',
    showLegends: false,
    filterType: 'periods',
  },
  SegmentDriversOnlyAnalysis3Level: {
    showLegends: true,
    filterType: 'segments',
  },
  DriversProgressionWithoutSentiments3Level: {
    type: 'SearchKeywords',
    showLegends: false,
    filterType: 'periods',
  },
  SegmentDriversOnlyAnalysisWithoutSentiments3Level: {
    type: 'SearchKeywords',
    showLegends: false,
    filterType: 'segments',
  },
}

export const useComparisonData = (props) => {
  const [state, setState] = useState([])
  const [segmentData, setSegmentData] = useState(null)
  const [reportData, setReportData] = useState([])
  const [maxValue, setMaxValue] = useState(100)
  const [segmentExcelData, setSegmentExcelData] = useState()
  const [periodInOrder, setPeriodInOrder] = useState()
  const [eventCount, setEventCount] = useState(1)
  const [progressionRangeArray, setProgressionRangeArray] = useState([])
  const [popupDropdownArray, setPopupDropdownArray] = useState([])
  const [keyOrdering, setKeyOrdering] = useState([])
  
  const { updateSubLhsApiLoaded } = useUpdateLhs()

  const hasTabs = props.isDriversProgression
  const useReactQueryObject = {
    payloadDetails: {
      queryId: [props.InsightsElementType, props.InsightsElementId, 'driverProgression'],
      urlFunc: hasTabs
        ? ReportsService.getDriverProgressionElement
        : ReportsService.GetSegmentDriversAnalysisWithDeepDiveElement,
      payload: { insightsElementId: props.InsightsElementId },
    },
  }
  const { data, isLoading, isSuccess, isError, error, refetch } = useConditionalQuery(useReactQueryObject, props)

  // Get driver data
  const getData = (dd, field = 'SegmentDrivers', isInner = true) => {
    const obj = {}
    const percentArr = []
    for (let index = 0; index < dd?.length; index++) {
      const element = dd[index]
      const driverElement = element?.[field]?.DriverElement
      const drivers = Array.isArray(driverElement)
        ? driverElement
        : Object.keys(driverElement || {})?.length
          ? [driverElement]
          : []
      for (let j = 0; j < drivers.length; j++) {
        const innerElement = drivers[j]
        const key = innerElement.DriverName

        const associationData = [
          {
            SearchKeywords: innerElement.Percent,
          },
        ]

        const multiBarData = [
          {
            Positive: innerElement.PositivePercent,
            Neutral: innerElement.NeutralPercent,
            Mixed: innerElement.MixedPercent,
            Negative: innerElement.NegativePercent,
          },
        ]

        const keyName = isInner ? innerElement.TargetSegment : element.PeriodName

        obj[key] = {
          ...(obj[key] || {}),
          [keyName]: {
            ...innerElement,
            data: CONFIG[props.InsightsElementType]?.type ? associationData : multiBarData,
          },
        }
        percentArr.push(innerElement.Percent)
      }
    }
    setMaxValue(Math.max(...percentArr))
    return obj
  }

  // Get values helper
  const getValues = (values) => {
    const getNumber = (val) => {
      return val?.value ? Number((val.value * 100).toFixed(0)) : 0
    }
    return {
      v1: getNumber(values[0]),
      v2: getNumber(values[1]),
      v3: getNumber(values[2]),
      v4: getNumber(values[3]),
    }
  }

  // Other keys helper
  const otherkeys = (obj, name2, values) => {
    const monthNames = {
      Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
      Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
    }
    const monthVal = (monthNames[name2.split(' ')[0]] + 1).toString().padStart(2, '0')
    const yearMonthVal =
      (parseInt(name2.split(' ')[1]) < 50
        ? 2000 + parseInt(name2.split(' ')[1])
        : name2.split(' ')[1]) +
      '-' +
      monthVal
    return {
      otherDetails: obj,
      yearMonthVal: yearMonthVal,
      label: name2,
      fullLabel: name2,
      values: getValues(values),
    }
  }

  // Update excel data
  const updateExcelData = (data) => {
    const arr = { drivers: [], attributes: [], subAttributes: [] }
    const popupDropdownArr = []
    const objArr1 = Object.keys(data)
    
    for (let i = 0; i < objArr1.length; i++) {
      const name1 = objArr1[i]
      const objArr2 = Object.keys(data[name1])
      
      for (let j = 0; j < objArr2.length; j++) {
        const name2 = objArr2[j]
        const obj = data[name1][name2]
        const values = exportExcel_addSegments(obj, true)
        
        arr.drivers.push([{ value: name1 }, { value: name2 }, ...values])
        popupDropdownArr.push({
          drivers: name1,
          attributes: 'Overview',
          subAttributes: 'Overview',
          excel: [{ value: name1 }, { value: name2 }, ...values],
          ...otherkeys(obj, name2, values),
        })

        const driverAttri = getObjectAsArray(obj?.DriverAttributes?.DriverAttributeElement || [])

        for (let k = 0; k < driverAttri.length; k++) {
          const attrValues = exportExcel_addSegments(driverAttri[k], true)
          arr.attributes.push([
            { value: name1 },
            { value: name2 },
            { value: driverAttri[k].AttributeName },
            ...attrValues,
          ])
          popupDropdownArr.push({
            drivers: name1,
            attributes: driverAttri[k].AttributeName,
            subAttributes: 'Overview',
            excel: [
              { value: name1 },
              { value: name2 },
              { value: driverAttri[k].AttributeName },
              ...attrValues,
            ],
            ...otherkeys(driverAttri[k], name2, attrValues),
          })

          let subDriverAttri = getObjectAsArray(
            driverAttri[k]?.DriverSubAttributes?.DriverSubAttributeElement || []
          )
          for (let m = 0; m < subDriverAttri.length; m++) {
            const subValues = exportExcel_addSegments(subDriverAttri[m], true)
            arr.subAttributes.push([
              { value: name1 },
              { value: name2 },
              { value: driverAttri[k].AttributeName },
              { value: subDriverAttri[m].SubAttributeName },
              ...subValues,
            ])
            popupDropdownArr.push({
              drivers: name1,
              attributes: driverAttri[k].AttributeName,
              subAttributes: subDriverAttri[m].SubAttributeName,
              excel: [
                { value: name1 },
                { value: name2 },
                { value: driverAttri[k].AttributeName },
                { value: subDriverAttri[m].SubAttributeName },
                ...subValues,
              ],
              ...otherkeys(subDriverAttri[m], name2, subValues),
            })
          }
        }
      }
    }

    setPopupDropdownArray(popupDropdownArr)
    setSegmentExcelData(arr)
  }

  // Patch function - removed, not needed anymore
  
  // Success callback
  const onSuccessFunc = (resp) => {
    const orderedData = sortByKey(getObjectAsArray(resp), 'PeriodOrder').map((d) => d.PeriodName)
    setPeriodInOrder(orderedData)
    
    if (hasTabs) {
      const sd = getData(resp, 'PeriodDrivers', false)
      setSegmentData(sd)
      updateExcelData(sd)

      const dd = resp.map((item) => ({
        ...item,
        SegmentDrivers: item.PeriodDrivers,
      }))
      setReportData(dd)
      updateSubLhsApiLoaded(props)
      setEventCount((p) => p + 1)
    } else {
      const averageStarRating = resp.map((item) => ({
        SegmentType: item.SegmentType,
        SegmentName: item.SegmentName,
        SegmentMaxValue: item.SegmentMaxValue,
        SegmentValue: item.SegmentValue,
        type: 'Association',
        data: [{ 0: item.SegmentValue }],
      }))
      setState([
        {
          SegmentType: averageStarRating[0]?.SegmentType,
          data: averageStarRating,
        },
      ])

      const sd = getData(resp)
      setSegmentData(sd)
      updateExcelData(sd)

      setReportData(resp)
      updateSubLhsApiLoaded(props)
      setEventCount((p) => p + 1)
    }

    const progressionArray = getProgressionArray(resp, props.InsightsElementType)
    setProgressionRangeArray(progressionArray)
    // setProgressionRangeArray(getProgressionArray(resp))
  }

  // On success effect
  useEffect(() => {
    if (isSuccess && data?.length) {
      onSuccessFunc(data)
    }
  }, [isSuccess, data])

  // Update key ordering
  useEffect(() => {
    setKeyOrdering(reportData.map((d) => d?.PeriodName || d?.SegmentName))
  }, [reportData])

  return {
    state,
    segmentData,
    reportData,
    maxValue,
    segmentExcelData,
    periodInOrder,
    eventCount,
    setEventCount,
    progressionRangeArray,
    popupDropdownArray,
    keyOrdering,
    isLoading,
    isSuccess,
    isError,
    error,
    refetch,
    CONFIG,
  }
}
